import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import WarningIcon from "@mui/icons-material/WarningRounded";
import { Divider, IconButton, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { createErrorApiResponse, withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { FileDownloadInfo } from "@entrilia/shared/api/fileTypes";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import FileIcon from "@entrilia/shared/components/FileIcon";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useToggleState from "@entrilia/shared/hooks/useToggleState";
import { logError } from "@entrilia/shared/logging";
import { formatFileSize, getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import adminApi from "../../api/adminApi";
import type { DataCatalogueEntityType } from "../../api/types/fileTypes";
import DocumentPreview from "../common/document-preview/DocumentPreview";
import type { PdfHighlight } from "../common/document-preview/PdfHighlightsPreview";
import DeleteDocumentDialog from "./DeleteDocumentDialog";
import PanelSection from "../common/layout/PanelSection";
import PanelSectionHeader from "../common/layout/PanelSectionHeader";
import ObjectCardContext from "./ObjectCardContext";
import ObjectCardDocumentUploadArea from "./ObjectCardDocumentUploadArea";

interface Props {
  dataCatalogEntityType: DataCatalogueEntityType;
  objectType: string;
  objectId: string;
  hasPermissionsToManageFiles: boolean;
  collapsible?: boolean;
  onDocumentDeleted: () => void;
}

const uploadEntityFile = withErrorHandling(adminApi.uploadEntityFile);

const ObjectCardDocumentPreviewSection = ({
  dataCatalogEntityType,
  objectType,
  objectId,
  hasPermissionsToManageFiles,
  collapsible,
  onDocumentDeleted,
}: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const { documentCitations, activeCitationKey } = useDefinedContext(ObjectCardContext, "ObjectCardContext");

  const [expanded, toggleExpanded] = useToggleState(true);

  const [uploadingFile, setUploadingFile] = useState(false);
  const [deleteDocumentDialogOpen, setDeleteDocumentDialogOpen] = useState(false);

  const getFiles = useCallback(
    () => adminApi.getEntityFiles(dataCatalogEntityType, objectId, objectType, { range: { take: 1 } }),
    [dataCatalogEntityType, objectId, objectType]
  );

  const [files, fetchError, { fetch: fetchFiles }] = useFetch(getFiles);

  // TODO Add support to display multiple files
  const firstFile = useMemo(() => files?.items[0], [files?.items]);

  const highlights = useMemo<PdfHighlight[]>(() => {
    if (!firstFile) {
      return [];
    }

    const result: PdfHighlight[] = [];
    for (const citation of documentCitations) {
      if (citation.sourceDataCatalogId !== firstFile.catalogueId) {
        continue;
      }

      for (const rect of citation.highlights) {
        result.push({
          citationId: citation.key,
          pageNumber: rect.pageNumber,
          leftPct: rect.leftPct,
          topPct: rect.topPct,
          widthPct: rect.widthPct,
          heightPct: rect.heightPct,
        });
      }
    }

    return result;
  }, [documentCitations, firstFile]);

  const highlightedCitationId = highlights.length > 0 ? activeCitationKey : undefined;

  const getFileInfo = useCallback(() => {
    const fileCatalogId = firstFile?.catalogueId;
    if (!fileCatalogId) {
      return Promise.resolve(createErrorApiResponse<FileDownloadInfo>({ message: "No file info available" }));
    }

    return adminApi.getEntityFileDownloadInfo(dataCatalogEntityType, objectId, objectType, fileCatalogId);
  }, [dataCatalogEntityType, firstFile, objectId, objectType]);

  if (fetchError) {
    logError(fetchError, "[ObjectCardDocumentPreviewSection]");
    return <DataLoadingFailed title="Failed to load notes" />;
  }

  const handleDeleteFile = () => {
    setDeleteDocumentDialogOpen(true);
  };

  const handleFileDeleted = () => {
    setDeleteDocumentDialogOpen(false);
    fetchFiles();
    onDocumentDeleted();
  };

  const handleUploadFile = async (file: File) => {
    setUploadingFile(true);
    const [, error] = await uploadEntityFile(dataCatalogEntityType, objectId, objectType, file);
    setUploadingFile(false);

    if (error) {
      logError(error, "[ObjectCardDocumentPreviewSection] uploadEntityFile");
      sendNotificationError(error.message);
      return;
    }

    fetchFiles();
  };

  return (
    <>
      <PanelSection
        isLoading={files === undefined || uploadingFile}
        sx={{ height: expanded ? "100%" : "auto", minHeight: expanded ? "50%" : undefined }}
      >
        <PanelSectionHeader
          title="Document Preview"
          expanded={expanded}
          onToggleExpanded={collapsible ? toggleExpanded : undefined}
        />

        {firstFile === undefined && expanded && <ObjectCardDocumentUploadArea onFileUpload={handleUploadFile} />}

        {firstFile !== undefined && expanded && (
          <>
            <Stack spacing={1} sx={{ px: 2 }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 0, flex: 1 }}>
                  <FileIcon fileExtension={getFileExtension(firstFile.fileName)} />
                  <Typography noWrap sx={{ minWidth: 0 }}>
                    {firstFile.fileName}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexShrink: 0 }}>
                  <Typography noWrap>{formatFileSize(firstFile.fileSize)}</Typography>
                  {hasPermissionsToManageFiles && (
                    <IconButton color="error" onClick={handleDeleteFile}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>
              </Stack>

              {(firstFile.warnings ?? []).map((warningText, index) => (
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <Stack key={index} direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <WarningIcon color="warning" />
                  <Typography variant="caption" sx={{ color: "warning.main" }}>
                    {warningText}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Divider flexItem />

            <DocumentPreview
              getFileDownloadInfoCallback={getFileInfo}
              highlights={highlights}
              selectedCitationId={highlightedCitationId}
            />
          </>
        )}
      </PanelSection>
      {firstFile !== undefined && (
        <DeleteDocumentDialog
          catalogueId={firstFile?.catalogueId}
          fileName={firstFile?.fileName}
          dataCatalogEntityType={dataCatalogEntityType}
          objectType={objectType}
          objectId={objectId}
          open={deleteDocumentDialogOpen}
          onClose={() => setDeleteDocumentDialogOpen(false)}
          onDeleted={handleFileDeleted}
        />
      )}
    </>
  );
};

export default ObjectCardDocumentPreviewSection;
